/**
 * Login page for Ops Dashboard.
 * Uses Roboticks split-screen layout matching the main app design.
 * Supports both email/password and Google OAuth authentication.
 */

import { useState, FormEvent } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { isAmplifyConfigured } from '../config/amplify';

export default function LoginPage() {
  const { login, isLoading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!email || !password) {
      setFormError('Please enter email and password');
      return;
    }

    try {
      await login(email, password);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setFormError(message);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      setFormError(null);
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setFormError('Failed to initiate Google sign-in');
      setGoogleLoading(false);
    }
  };

  const showOAuth = isAmplifyConfigured();

  return (
    <Grid container sx={{ minHeight: '100vh' }}>
      {/* Left side - Branding */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 6,
        }}
      >
        <Box sx={{ maxWidth: 450 }}>
          <img
            src="/roboticks-logo.svg"
            alt="Roboticks"
            style={{ height: 48, width: 'auto' }}
          />
          <Typography
            variant="h5"
            sx={{ color: 'white', mt: 4, fontWeight: 500 }}
          >
            Operations Dashboard
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255,255,255,0.8)', mt: 2 }}
          >
            Manage deployments, monitor system health, and respond to incidents
            across all Roboticks environments.
          </Typography>
        </Box>
      </Grid>

      {/* Right side - Login Form */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          p: 4,
        }}
      >
        <Box sx={{ maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" fontWeight={600} fontSize={{ sm: 30, xs: 25 }}>
            Sign In
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 4 }}>
            Access restricted to members of the Roboticks team.
          </Typography>

          {(error || formError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || formError}
            </Alert>
          )}

          {/* Google Sign-In - Primary for @roboticks.io users */}
          {showOAuth && (
            <>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={googleLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
                onClick={handleGoogleLogin}
                disabled={googleLoading || isLoading}
                sx={{
                  py: 1.5,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {googleLoading ? 'Redirecting...' : 'Continue with Google'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  or sign in with email
                </Typography>
              </Divider>
            </>
          )}

          <form onSubmit={handleSubmit}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="you@roboticks.io"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || googleLoading}
              sx={{ mb: 3 }}
              autoComplete="email"
            />

            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || googleLoading}
              sx={{ mb: 3 }}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading || googleLoading}
              sx={{ py: 1.5 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Need access? Contact your Roboticks administrator.
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
